
import React, { useEffect, useRef, useState } from "react";
import Styles from "../../styles/exercise/exercisereport.module.scss";
import Navbar from "../../components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggle } from "../../redux";
import { coachRoutes } from "../../routers/CoachRouters";
import {
  addBiometricReport,
  getClientBlockData,
  getClients,
} from "../../services";
import { updateBiometricReportByReportId } from "../../services/biometricService";
import Close_Button from "../../assets/auth/close.png";
import Suceess from "../../assets/auth/success.png";
import Swal from "sweetalert2";

const BiometricReport = () => {
  // refs for biometric fields
  const blockRef = useRef();
  const bodyWeightRef = useRef();
  const muscleRef = useRef();
  const bodyFatRef = useRef();
  const notesRef = useRef();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSidebarOpen = useSelector((s) => s.navbar.button);
  const { clients } = useSelector((s) => s.client);

  // form states
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // new local states
  const [blockData, setBlockData] = useState(null);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);

  // Fix: Local state for immediate client updates
  const [localClients, setLocalClients] = useState([]);
  const [abortController, setAbortController] = useState(null);

  const blockOptions = [1, 2, 3, 4];

  // Fix: Consistent API call function
  const fetchClients = async (searchTerm = "") => {
    // Cancel previous request if exists
    if (abortController) {
      abortController.abort();
    }

    const controller = new AbortController();
    setAbortController(controller);
    setIsSearching(true);

    try {
      const response = await getClients(false, 1, -1, searchTerm, "latest", {
        signal: controller.signal,
      });

      // Update local state immediately with the response
      if (response && response.data) {
        setLocalClients(response.data);
        console.log("Fetched clients:", response.data);
      } else if (response && Array.isArray(response)) {
        setLocalClients(response);
        console.log("Fetched clients (direct array):", response);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching clients:", error);
      }
    } finally {
      setIsSearching(false);
      setAbortController(null);
    }
  };

  // initial clients load
  useEffect(() => {
    fetchClients();
  }, []);

  // Fix: Updated filtering logic with both local and Redux clients
  useEffect(() => {
    // Use localClients if available, otherwise fall back to Redux clients
    const clientsToFilter =
      localClients.length > 0 ? localClients : clients || [];

    console.log("Filtering clients:", {
      localClientsCount: localClients.length,
      reduxClientsCount: clients?.length || 0,
      searchTerm: clientSearch,
      sampleClient: clientsToFilter[0],
    });

    if (clientsToFilter && clientSearch) {
      const q = clientSearch.toLowerCase();
      const filtered = clientsToFilter.filter((c) => {
        const name = (c.name || "").toLowerCase();
        const code = (c.code || "").toLowerCase();
        const email = (c.email || "").toLowerCase();

        const matches =
          name.includes(q) || code.includes(q) || email.includes(q);

        if (matches) {
          console.log("Client matches search:", {
            name: c.name,
            email: c.email,
            code: c.code,
          });
        }

        return matches;
      });
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clientsToFilter);
    }
  }, [clientSearch, clients, localClients]);

  // whenever both client & block are set, fetch that block's data
  useEffect(() => {
    if (selectedClient && selectedBlock) fetchClientBlockData();
  }, [selectedClient, selectedBlock]);

  const fetchClientBlockData = async () => {
    setIsLoadingData(true);
    try {
      const data = await getClientBlockData(selectedClient._id, selectedBlock);
      setBlockData(data || null);

      const hasBody = Boolean(data && data.body);
      setFieldsDisabled(hasBody);

      // fill or clear the refs
      if (hasBody) {
        bodyWeightRef.current.value = data.body.weight ?? "";
        muscleRef.current.value = data.body.muscle ?? "";
        bodyFatRef.current.value = data.body.fat ?? "";
        notesRef.current.value = data.notes || "";
      } else {
        bodyWeightRef.current.value = "";
        muscleRef.current.value = "";
        bodyFatRef.current.value = "";
        notesRef.current.value = "";
      }
    } catch (err) {
      console.error("Error fetching client block data:", err);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Fix: Updated client search handler
  const handleClientSearch = (v) => {
    setClientSearch(v);
    setShowClientDropdown(true);
    setSelectedClient(null);

    if (v.trim()) {
      fetchClients(v.trim());
    } else {
      fetchClients(); // Fetch all clients when search is empty
    }
  };

  const handleClientSelect = (c) => {
    console.log("Selected client:", c);
    setSelectedClient(c);
    setClientSearch(c.name || "");
    setShowClientDropdown(false);
  };

  // Fix: Updated focus handler
  const handleClientInputFocus = () => {
    setShowClientDropdown(true);
    // Only fetch if we don't have clients loaded
    if (
      !isSearching &&
      localClients.length === 0 &&
      (!clients || clients.length === 0)
    ) {
      fetchClients();
    }
  };

  const handleClientInputBlur = () =>
    setTimeout(() => setShowClientDropdown(false), 200);

  const handleBlockChange = (v) => setSelectedBlock(v);
  const handleMainContainerClick = () => isSidebarOpen && dispatch(toggle());

  const bodyExists = Boolean(blockData && blockData.body);
  const workoutsExist = Boolean(
    blockData && blockData.workouts && blockData.workouts.length > 0
  );
  const saveButtonText =
    bodyExists && !workoutsExist ? "Create Exercise Report" : "Save";
  const showSave = !bodyExists || (bodyExists && !workoutsExist);
  const showSaveShare = !(bodyExists && workoutsExist);
  const showShare = bodyExists && workoutsExist;
  const handleWheelPrevent = (e) => {
    e.target.blur();
    e.preventDefault();
  };
  // submit logic
  const handleSubmit = async () => {
    if (!selectedClient || !selectedBlock) return;
    const toNum = (val) => {
      if (!val) return null;
      const n = parseFloat(val);
      return isNaN(n) ? null : n;
    };
    const formData = {
      client: selectedClient._id,
      block: parseInt(selectedBlock, 10),
      body: {
        weight: toNum(bodyWeightRef.current.value),
        muscle: toNum(muscleRef.current.value),
        fat: toNum(bodyFatRef.current.value),
      },
      notes: notesRef.current.value || "",
    };
    try {
      if (blockData && blockData.body) {
        const ok = await updateBiometricReportByReportId(
          blockData._id,
          formData
        );
        if (ok) {
          showSuccessModal();
        }
      } else {
        const ok = await addBiometricReport(formData);
        if (ok) {
          showSuccessModal();
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const showSuccessModal = () => {
    Swal.fire({
      html: `
        <div style="text-align: center; border-radius: 20px; position: relative;">
         <button 
            id="customCloseButton" 
            style="position: absolute; top:-10px; right:-20px; background: none; border: none; cursor: pointer;">
            <img 
            src=${Close_Button} 
              alt="Close" 
              style="width: 50px; height: 50px;"
            />
          </button>
          <img 
            src=${Suceess} 
            alt="Success" 
            style="width: 100px; margin-bottom: 20px;margin-top:30px;" 
          />
         
          
        <p style="color: #151515; text-align:center; font-family:gothambold; font-size:24px;">
           Biometric Report Successfully Created
          </p>

<br/>
        <button 
            id="ok" 
style="background: white; color: #151515;  padding: 15px 20px; font-family:gothambold; border-radius: 8px; cursor: pointer; margin-top: 20px;border: 1px solid #E27808;width:250px;height:48px;font-size:14px;">
ok  </button>
        </div>
      `,
      showConfirmButton: false,
      customClass: {
        popup: "custom-swal-container",
      },
      didOpen: () => {
        const ok = document.getElementById("ok");
        const closeButton = document.getElementById("customCloseButton");

        ok.addEventListener("click", () => {
          Swal.close();
          navigate(coachRoutes.listClient);
        });

        closeButton.addEventListener("click", () => {
          Swal.close();
          navigate(coachRoutes.listClient);
        });
      },
    });
  };

  const handlePrimaryAction = () => {
    if (saveButtonText === "Create Exercise Report") {
      navigate(coachRoutes.createBiometricExercise);
    } else {
      handleSubmit();
    }
  };
  const handleSaveAndShare = handleSubmit;
  const handleShare = handleSubmit;
  const handleCancel = () => navigate(coachRoutes.dashboard);

  // Fix: Better input change handler to ensure proper state updates
  const handleClientInputChange = (e) => {
    const value = e.target.value;
    handleClientSearch(value);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  return (
    <>
      <Navbar />
      <div
        className={Styles.createsession_main}
        onClick={handleMainContainerClick}
      >
        <div className={Styles.createsession_container}>
          <h2>
            <Link to={coachRoutes.dashboard}>HOME / </Link>
            <span className={Styles.orange}>CREATE BIOMETRIC</span>
          </h2>
          <div className={Styles.form_container}>
            <div className={Styles.table_container_headingone}>
              <h3>CREATE BIOMETRIC</h3>
            </div>

            {/* ── Client + Block Row ── */}
            <div className={Styles.row_padding}>
              <div className={Styles.row}>
                {/* Client */}
                <div
                  className={Styles.input_group}
                  style={{ position: "relative" }}
                >
                  <label className={Styles.label}>ID/Name</label>
                  <input
                    type="text"
                    placeholder="Enter here"
                    value={clientSearch}
                    onChange={handleClientInputChange}
                    onFocus={handleClientInputFocus}
                    onBlur={handleClientInputBlur}
                    required
                  />
                  {isSearching && (
                    <div className={Styles.search_loading}>
                      <span>Searching...</span>
                    </div>
                  )}
                  {showClientDropdown && !isSearching && (
                    <div className={Styles.client_dropdown}>
                      {filteredClients.length > 0 ? (
                        filteredClients.map((c) => (
                          <div
                            key={c._id}
                            className={Styles.client_option}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleClientSelect(c)}
                          >
                            <div className={Styles.client_name}>
                              {c.name || "--"}
                            </div>
                            <div className={Styles.client_details}>
                              {c.code || "--"} - {c.email || "--"}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={Styles.no_results}>
                          No clients found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Block */}
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Block</label>
                  <select
                    ref={blockRef}
                    className={Styles.dropdown_select}
                    value={selectedBlock}
                    onChange={(e) => handleBlockChange(e.target.value)}
                  >
                    <option value="" disabled>
                      Choose from list
                    </option>
                    {blockOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        Block {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {isLoadingData && (
                <div className={Styles.loading_indicator}>
                  <p>Loading data...</p>
                </div>
              )}

              {/* ── Biometric Inputs ── */}
              <div className={Styles.row}>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Body Weight (kg)</label>
                  <input
                    ref={bodyWeightRef}
                    type="number"
                    placeholder="Enter here"
                    disabled={fieldsDisabled}
                    onWheel={handleWheelPrevent}
                  />
                </div>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Muscle (kg)</label>
                  <input
                    ref={muscleRef}
                    type="number"
                    placeholder="Enter here"
                    disabled={fieldsDisabled}
                    onWheel={handleWheelPrevent}
                  />
                </div>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Body Fat (%)</label>
                  <input
                    ref={bodyFatRef}
                    type="number"
                    step="0.1"
                    placeholder="Enter here"
                    disabled={fieldsDisabled}
                    onWheel={handleWheelPrevent}
                  />
                </div>
              </div>

              <div className={Styles.notes_section}>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Notes</label>
                  <textarea
                    ref={notesRef}
                    className={Styles.notes_textarea}
                    placeholder="Enter additional notes here..."
                    rows="4"
                    disabled={fieldsDisabled}
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className={Styles.input_radio}>
                <label>
                  <input
                    type="checkbox"
                    name="biometrics"
                    checked={bodyExists}
                    disabled={true}
                  />
                  <span></span>
                  Biometrics
                </label>
                <label>
                  <input
                    name="exercise"
                    type="checkbox"
                    checked={workoutsExist}
                    disabled={true}
                  />{" "}
                  <span></span>
                  Exercise
                </label>
              </div>
            </div>

            <div className={Styles.button_group_line}></div>

            {/* ── Action Buttons ── */}
            <div className={Styles.button_group}>
              {showSave && (
                <button
                  className={Styles.save_button}
                  onClick={handlePrimaryAction}
                >
                  {saveButtonText}
                </button>
              )}
              {showSaveShare && (
                <button
                  className={Styles.save_share_button}
                  onClick={handleSaveAndShare}
                  disabled={!(bodyExists && workoutsExist)}
                >
                  Save &amp; Share
                </button>
              )}
              {showShare && (
                <button
                  className={Styles.save_share_button}
                  onClick={handleShare}
                >
                  Save &amp; Share
                </button>
              )}
              <button className={Styles.cancel_button} onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BiometricReport;
